// AnalyticsContext.jsx
import { createContext, useContext, useReducer, useMemo, useCallback } from 'react'
import restClient from 'Shared/hooks/restClient'
import waitForDeferredResult from "Shared/utils/waitForDeferredResult"
import analyticsReducer from 'AnalyticsDashboard/context/analyticsReducer'

const AnalyticsContext = createContext()

export { AnalyticsContext, analyticsReducer }

function validateQueryParams(params, queryInfo) {
  const errors = []

  // Validate dimensions exist in available dimensions
  if (params.dimensions && queryInfo?.availableDimensions) {
    const availableDimensionIds = queryInfo.availableDimensions.map(d => d.id)

    for (const dimension of params.dimensions) {
      if (!availableDimensionIds.includes(dimension.id)) {
        errors.push(`Unknown dimension: "${dimension.id}". Available dimensions: ${availableDimensionIds.join(', ')}`)
      }
    }
  }

  // Validate metrics exist in available metrics
  if (params.metrics && queryInfo?.availableMetrics) {
    const availableMetricIds = queryInfo.availableMetrics.map(m => m.id)

    for (const metric of params.metrics) {
      if (!availableMetricIds.includes(metric.id)) {
        errors.push(`Unknown metric: "${metric.id}". Available metrics: ${availableMetricIds.join(', ')}`)
      }
    }
  }

  if (errors.length > 0) {
    throw new Error(`Analytics query validation failed:\n${errors.join('\n')}`)
  }
}

export function useAnalyticsContext() {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error('useAnalyticsContext must be used within AnalyticsProvider')
  }
  return context
}

export function useFilters() {
  const { state, dispatch } = useAnalyticsContext()

  const isGlobal = _.some(_.values(state.queries), q => q?.global)
  const clientIds = state.filterValues?.client_id || []
  const singleClient = !isGlobal || (Array.isArray(clientIds) && clientIds.length === 1)

  const allFilters = _.map(_.values(state.queries), query => {
    if (!query) return []
    const queryInfo = state.queriesInfo.find(q => q.kind === query.kind)

    return queryInfo.availableFilters
  })
  const uniqueFilters = _.uniqBy(_.flatten(allFilters), filter => `${filter.id}-${filter.kind}`)
    .filter(filter => !filter.clientOnly || singleClient)

  const allSegmentDimensions = _.map(_.values(state.queries), query => {
    return query?.queryInfo?.availableSegmentDimensions || []
  })
  const uniqueSegmentDimensions = _.uniqBy(_.flatten(allSegmentDimensions), dim => dim.id)

  return {
    filters: uniqueFilters,
    segmentDimensions: uniqueSegmentDimensions,
  }
}

export function useAnalyticsQuery(id) {
  const { state, dispatch } = useAnalyticsContext()

  const foundQuery = state.queries[id]
  var query = null
  if (foundQuery) {
    query = { ...foundQuery, queryInfo: state.queriesInfo.find(q => q.kind === foundQuery.kind) }
  }

  const updateQuery = useCallback((data) => {
    dispatch({
      type: 'UPDATE_QUERY',
      payload: { id, data }
    })
  }, [dispatch, id])

  const setQueryData = useCallback((data) => {
    dispatch({
      type: 'SET_QUERY_DATA',
      payload: { id, data }
    })
  }, [dispatch, id])

  const setQueryDataLoading = useCallback((loading) => {
    dispatch({
      type: 'SET_QUERY_DATA_LOADING',
      payload: { id, loading }
    })
  }, [dispatch, id])

  const updateDimension = useCallback((dimensionId, data) => {
    dispatch({
      type: 'UPDATE_DIMENSION',
      payload: { id, dimensionId, data }
    })
  }, [dispatch, id])

  const effectiveFilters = state.sharedFiltersEnabled && query?.filtersEnabled
    ? state.sharedFilters
    : query?.filterValues || []

  const effectiveTimeDimension = state.sharedFiltersEnabled && query?.filtersEnabled
    ? state.sharedTimeDimension
    : query?.timeDimension

  return {
    query,
    sharedFilters: state.sharedFilters,
    sharedTimeDimension: state.sharedTimeDimension,
    sharedFiltersEnabled: state.sharedFiltersEnabled,
    effectiveFilters,
    effectiveTimeDimension,
    updateQuery,
    updateDimension,
    setQueryData,
    setQueryDataLoading
  }
}

export function useAnalyticsDashboard() {
  const { state, dispatch } = useAnalyticsContext()
  const { filters, segmentDimensions } = useFilters()

  const fetchQueriesInfo = useCallback(async () => {
    console.log('Fetching analytics queries info')
    const params = state.global ? { global: true } : {}
    const response = await restClient.get('/api/analytics/analytics_queries', { params })
    dispatch({
      type: 'SET_QUERIES_INFO',
      payload: response.data
    })
  }, [dispatch, state.global])

  const fetchDataAll = useCallback(async () => {
    const queryIds = Object.keys(state.queries)
    for (const queryId of queryIds) {
      fetchData(queryId)
    }
  })

  const fetchData = async (queryId) => {
    const { kind, dimensions = [], metrics, parameters = {}, global: queryGlobal = false, overrideFilters = {}, limit = null, orderBy = [], comparePeriods = null, queryInfo } = state.queries[queryId]
    const global = queryGlobal || state.global

    dispatch({
      type: 'UPDATE_QUERY',
      payload: { id: queryId, data: { dataLoading: true, firstFetch: false } }
    })

    // we store as map, but need to convert to array for API
    const filterValues = _.map({ ...state.filterValues, ...overrideFilters }, (value, id) => {
      return { id, value }
    }).filter(fv => fv.value !== undefined && fv.value !== null && !(Array.isArray(fv.value) && fv.value.length === 0))

    const snakeMetrics = metrics.map(m => ({ ...m, id: _.snakeCase(m.id) }))
    const params = {
      kind,
      filterValues,
      metrics: snakeMetrics,
      dimensions,
      parameters,
      global,
      orderBy,
      limit,
      ...(comparePeriods ? { comparePeriods } : {})
    }

    validateQueryParams(params, queryInfo)
    const result = await restClient.post(`/api/analytics/analytics_queries/run_query`, params)
    const deferredResultId = result.data.deferredResultId
    const data = await waitForDeferredResult(deferredResultId)

    dispatch({
      type: 'UPDATE_QUERY',
      payload: { id: queryId, data: { data, dataLoading: false } }
    })
  }

  const registerQuery = useCallback((payload) => {
    dispatch({
      type: 'REGISTER_QUERY',
      payload
    })
  }, [dispatch])

  const unregisterQuery = useCallback((id) => {
    dispatch({
      type: 'UNREGISTER_QUERY',
      payload: { id }
    })
  }, [dispatch])

  const setFilterValue = useCallback((id, value) => {
    dispatch({
      type: 'SET_FILTER',
      payload: { id, value }
    })
  }, [dispatch])

  const setTimeDimension = useCallback((value) => {
    dispatch({
      type: 'SET_TIME_DIMENSION',
      payload: { value }
    })
  }, [dispatch])

  const toggleSegmentDimension = useCallback((id) => {
    dispatch({
      type: 'TOGGLE_SEGMENT_DIMENSION',
      payload: { id }
    })
  }, [dispatch])

  return {
    fetchQueriesInfo,
    filters,
    segmentDimensions,
    dimensions: state.dimensions,
    primaryDimension: state.primaryDimension,
    filterValues: state.filterValues,
    registerQuery,
    unregisterQuery,
    setFilterValue,
    setTimeDimension,
    toggleSegmentDimension,
    fetchData,
    fetchDataAll
  }
}
